/**
 * Service lines.
 *
 * This is marketing copy, not a contract - edit freely. Keep `summary` to a single
 * sentence: it is what renders on the home page, where space is tight.
 */

export interface Service {
  readonly id: string;
  readonly title: string;
  /** One sentence. Appears on the home page grid. */
  readonly summary: string;
  /** Two or three sentences. Appears on the services page. */
  readonly detail: string;
  /** Concrete deliverables - what a client actually receives. */
  readonly deliverables: readonly string[];
}

export const services: readonly Service[] = [
  {
    id: 'product-engineering',
    title: 'Product engineering',
    summary: 'We build the whole thing and get it live - not a demo you have to rebuild later.',
    detail:
      'From an empty repository to something real, in production, with people using it. We work in TypeScript, Go and Python on Google Cloud, and we treat tests, pipelines and monitoring as part of building it rather than a phase that quietly gets dropped.',
    deliverables: [
      'Production web and mobile-web applications',
      'APIs and backend services',
      'Automated test suites and CI pipelines',
      'Infrastructure as code',
    ],
  },
  {
    id: 'ai-systems',
    title: 'AI systems',
    summary:
      'Language models pointed at a real job, plus the evidence that they are doing it well.',
    detail:
      'Most AI projects stall somewhere between the demo everyone loved and the thing anyone can use. We enjoy the unglamorous half: retrieval that finds the right document, agents that cannot reach further than you meant, and evaluations that tell you honestly whether a change helped.',
    deliverables: [
      'Retrieval and knowledge-base systems',
      'Agentic workflows and tool integrations',
      'Evaluation harnesses and regression suites',
      'Cost, latency and quality benchmarking',
    ],
  },
  {
    id: 'optimisation-and-data',
    title: 'Optimisation and data',
    summary: 'Routing, scheduling and allocation puzzles, solved against how you actually operate.',
    detail:
      'Some problems will not yield to more people or a better spreadsheet. Vehicle routing, shift scheduling and capacity allocation are genuinely solvable - but only against your real constraints and your real data. We model the problem honestly, then build you a solver your team can drive.',
    deliverables: [
      'Constraint models built from your operating rules',
      'Route and schedule optimisation engines',
      'Benchmarking against current manual performance',
      'Operational dashboards and reporting',
    ],
  },
  {
    id: 'platform-and-delivery',
    title: 'Platform and delivery',
    summary: 'The pipeline and cloud setup that let your team ship without holding their breath.',
    detail:
      'Teams rarely slow down because people stopped caring. They slow down because deploys are frightening, environments drift and nobody quite trusts the tests. We fix the path to production itself, then hand it over properly documented.',
    deliverables: [
      'CI/CD pipelines with keyless cloud authentication',
      'Terraform-managed GCP infrastructure',
      'Monitoring, alerting and incident runbooks',
      'Cloud cost review and reduction',
    ],
  },
];
